import React, { useState } from "react";
import Box from '@mui/material/Box';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import Accordion from 'react-bootstrap/Accordion';
import Form from 'react-bootstrap/Form';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import CircularProgress from '@mui/material/CircularProgress';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Avatar from '@mui/material/Avatar';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import InvertColorsIcon from '@mui/icons-material/InvertColors';
import ConstructionIcon from '@mui/icons-material/Construction';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import MiscellaneousServicesIcon from '@mui/icons-material/MiscellaneousServices';
import useAxiosProtect from "../../../../../hooks/useAxiosProtect";
import EventInfoModal from "./EventInfoModal";
import '../styling/dataPanelStyle.css';
import { electricUnitTypes, gasUnitTypes, waterUnitTypes, steamUnitTypes, temperatureUnitTypes } from "../../../../../example_data/dropdowns/clientModal/loggingComponents";

const DataPanel = props => {
    const type = 'checkbox';

    const [currentSite, setCurrentSite] = React.useState(window.location.href.split('/').slice(-1));

    const axiosProtect = useAxiosProtect();
    const [eventData, setEventData] = useState(null);

    const handleChange = e => {
        if(e.target.value == currentSite) return;
        window.history.replaceState({}, '', `/energy-metrics/${e.target.value}`);
        props.resetAuthCheck();
    };

    const toggleAccordionKey = key => {
        props.setAccordionKey(props.accordionKey === key ? null : key);
    }

    const formatEventTime = dateString => {
        const date = new Date(dateString);

        const localDate = new Date(date.getTime());
    
        const day = String(localDate.getDate()).padStart(2, '0');
        const month = localDate.toLocaleString('default', { month: 'long' });
        const year = localDate.getFullYear();
        const hour = String(localDate.getHours()).padStart(2, '0');
        const minute = String(localDate.getMinutes()).padStart(2, '0');
    
        return `${day} ${month} ${year} ${hour}:${minute}`;
    }

    const removeEvent = eventID => {
        props.setEvents(prev => prev.filter(ev => ev.id != eventID));
    }

    const getInsightName = comp => {
        let totalCheck = comp.split('-');

        if(totalCheck.length === 2 && totalCheck[0] === 'total'){
            let unitLookup = props.plotSpecs.activeUtility === 'gas' ? gasUnitTypes : 
                props.plotSpecs.activeUtility === 'water' ? waterUnitTypes : 
                props.plotSpecs.activeUtility === 'electricity' ? electricUnitTypes : 
                props.plotSpecs.activeUtility === 'steam' ? steamUnitTypes : temperatureUnitTypes;
            
            if(unitLookup.some(u => u.value === totalCheck[1])){
                return unitLookup.find(u => u.value === totalCheck[1])?.text ?? totalCheck[1];
            }
        }

        return comp;
    }

    const determineUnits = isTotal => {
        if(isTotal){
            if(props.plotSpecs.activeUtility === 'water'){
                return props.plotSpecs.metricType.startsWith('L') ? 'L' : 'm³'

            }else if(props.plotSpecs.activeUtility === 'electricity'){
                return props.plotSpecs.metricType === 'Current (A)' ? 'A' : props.plotSpecs.metricType;

            }else if(props.plotSpecs.activeUtility === 'gas'){
                return 'm³';

            }else if(props.plotSpecs.activeUtility === 'temperature'){
                return props.plotSpecs.metricType;
            }
        }

        return props.plotSpecs.metricType === 'Current (A)' ? 'A' : props.plotSpecs.metricType;
    }

    return(
        <>
        {
            props.isClient &&
            <Box sx={{ minWidth: 120, marginBottom: '1vh', marginTop: '1vh'}}>
                <FormControl fullWidth>
                    <InputLabel id="demo-simple-select-label">Site</InputLabel>
                    <Select
                        value={currentSite}
                        label="Site"
                        onChange={handleChange}
                    >
                        {
                            props.sites.map((site, i) => (
                                <MenuItem value={site.id.toString()}>{site.name}</MenuItem>
                            ))
                        }
                    </Select>
                </FormControl>
            </Box>
        }

        {props.eventInfoModalOpen && <EventInfoModal eventInfoModalOpen={props.eventInfoModalOpen} 
            setEventInfoModalOpen={props.setEventInfoModalOpen} eventData={eventData} formatEventTime={formatEventTime} 
            removeEvent={removeEvent} setPlotSpecs={props.setPlotSpecs} toolLoad={props.toolLoad} setData={props.setData} 
            setErrorMessage={props.setErrorMessage} setSelectedDates={props.setSelectedDates} updatePlotSpecs={props.updatePlotSpecs}/>}

        <Box sx={{width: '100%'}}>
            <FormControl fullWidth margin="dense" disabled={
                props.plotSpecCooldown &&
                (props.data == null ? true : props.data.length === 0)
            }>
                <InputLabel id="demo-simple-select-label">Utility</InputLabel>
                <Select
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    value={props.plotSpecs.activeUtility}
                    label="Utility"
                    disabled={
                        props.plotSpecCooldown &&
                        (props.data == null ? true : props.data.length === 0)
                    }
                    onChange={e => props.updatePlotSpecs(e, 'activeUtility')}
                >
                    <MenuItem value='water' disabled={!props.scopeData.hasWater}>Water</MenuItem>
                    <MenuItem value='electricity' disabled={!props.scopeData.hasElec}>Electricity</MenuItem>
                    <MenuItem value='gas' disabled={!props.scopeData.hasGas}>Gas</MenuItem>
                    <MenuItem value='steam'>Steam</MenuItem>
                    <MenuItem value='temperature'>Temperature</MenuItem>
                </Select>
            </FormControl>
        </Box>

        {props.plotSpecs.activeUtility === 'steam' && <p style={{fontSize:'0.85em', marginTop:'0.5rem'}}>Steam energy rate uses saturated-vapor enthalpy (h<sub>g</sub>) at the sensor’s configured operating pressure.</p>}

        <FormControl style={{ margin: '1vh 0' }} disabled={
            props.plotSpecCooldown &&
            (props.data == null ? true : props.data.length === 0)
        }>
            <FormLabel id="device-supplier-label">Data type</FormLabel>
            <RadioGroup
                row
                aria-labelledby="device-supplier-label"
                name="device-supplier-group"
                value={props.plotSpecs.dataFetchMethod}
                onChange={e => props.updatePlotSpecs(e, 'dataFetchMethod')}
            >
                <FormControlLabel value="adjusted" control={<Radio />} label="Adjusted" />
                <FormControlLabel value="raw" control={<Radio />} label="Raw" />
            </RadioGroup>
        </FormControl>

        <Accordion activeKey={props.accordionKey}>
            <Accordion.Item eventKey="0">
                <Accordion.Header onClick={() => toggleAccordionKey('0')}>{`Data sets (${props.loggedUnits.length})`}</Accordion.Header>
                <Accordion.Body id='raw-data-datapanel-custom-scrollbar' style={{height:`${props.isClient ? '32vh' : '39vh'}`, overflowY:'scroll'}}>
                    {
                        (props.data == null ? true : props.data.length === 0) ? 
                        <Box sx={{ display: 'flex' }}>
                            <CircularProgress style={{margin:'auto'}}/>
                        </Box>
                        :
                        <List
                            sx={{ width: '100%',bgcolor: 'background.paper' }}
                            component="nav"
                            aria-labelledby="nested-list-subheader"
                            disablePadding
                        >

                            {(() => {
                                const key = props.plotSpecs.activeUtility === 'gas' ? 'metered_unit_type' : 'unit_type';

                                let categories = [];

                                //put in try catch block for async utilityType changes
                                try{
                                    categories = [...new Set(
                                        (props.loggedUnits ?? [])
                                        .filter(o => o?.isCategory === true)
                                        .filter(Boolean)
                                    )].sort((a,b) => (b[key] === 'main') - (a[key] === 'main') || a[key].localeCompare(b[key]));
                                }catch(error){}

                                return categories.map((category, i)=> (
                                    <>
                                    <ListItem onClick={() => {}} disableGutters disablePadding>
                                        <Form.Check
                                            type={type}
                                            id={`default-${type}`}
                                            label={
                                                `${(props.plotSpecs.activeUtility === 'gas' ? gasUnitTypes : props.plotSpecs.activeUtility === 'water' ? waterUnitTypes : props.plotSpecs.activeUtility === 'electricity' ? electricUnitTypes : props.plotSpecs.activeUtility === 'steam' ? steamUnitTypes : temperatureUnitTypes).find(u => u.value === category[key])?.text ?? category[key]}`
                                            }
                                            checked={category.checked}
                                            onChange={() => props.toggleCheckedUnit(category.id)}
                                            className='data-checkbox'
                                        />

                                        <IconButton onClick={() => props.toggleOpenCategory(category.id)}>
                                            {category.opened ? <ExpandLess /> : <ExpandMore/>}
                                        </IconButton>
                                    </ListItem>

                                    <Collapse in={category.opened} key={category.name} timeout="auto" unmountOnExit>
                                        <List component="div" disablePadding>
                                            <ListItem key={`${type}-select-all`} style={{backgroundColor:'rgba(8,114,182,0.05)'}}>
                                                <Form.Check
                                                    style={{marginLeft:'5%'}}
                                                    type={type}
                                                    id={`default-${type}`}
                                                    label={`SELECT ALL`}
                                                    checked={
                                                        (props.loggedUnits ?? []).filter(o => o?.[key] === category[key] && !o?.isCategory).length > 0 &&
                                                        (props.loggedUnits ?? []).filter(o => o?.[key] === category[key] && !o?.isCategory && !o?.checked).length === 0
                                                    }
                                                    onChange={() => props.toggleAllUnitsInCategory(category.unit_type, 
                                                        !((props.loggedUnits ?? []).filter(o => o?.[key] === category[key] && !o?.isCategory).length > 0 &&
                                                        (props.loggedUnits ?? []).filter(o => o?.[key] === category[key] && !o?.isCategory && !o?.checked).length === 0)
                                                    )}
                                                    className='data-checkbox'
                                                />
                                            </ListItem>

                                            {(props.loggedUnits ?? [])
                                            .filter(o => o?.[key] === category[key] && !o?.isCategory)
                                            .toSorted((a,b) => (a.name ?? '').localeCompare(b.name ?? '', undefined, { numeric:true, sensitivity:'base' }))
                                            .map(unit => (
                                                <ListItem key={unit._id ?? unit.name} style={{backgroundColor:'rgba(8,114,182,0.05)'}}>
                                                    <Form.Check
                                                        style={{marginLeft:'5%'}}
                                                        type={type}
                                                        id={`default-${type}`}
                                                        label={`${unit.name}${props.plotSpecs.activeUtility === 'gas' ? ` [${[unit.gas_name || unit.sub_category_name, unit.gas_units || unit.unit_symbol].filter(Boolean).join(', ')}]` : ''}`}
                                                        checked={unit.checked}
                                                        onChange={() => props.toggleCheckedUnit(unit.id)}
                                                        className='data-checkbox'
                                                    />
                                                </ListItem>
                                            ))}
                                        </List>
                                    </Collapse>
                                    </>
                                ));
                            })()}
                        </List>
                    }
                </Accordion.Body>
            </Accordion.Item>

            <Accordion.Item eventKey="1">
                <Accordion.Header onClick={() => toggleAccordionKey('1')}>{`Events (${props.events.length})`}</Accordion.Header>
                <Accordion.Body style={{height:`${props.isClient ? '32vh' : '39vh'}`, overflowY:'scroll', scrollbarWidth: 'none', msOverflowStyle: 'none'}}>
                    {
                        (props.data == null ? true : props.data.length === 0) ? 
                        <Box sx={{ display: 'flex' }}>
                            <CircularProgress style={{margin:'auto'}}/>
                        </Box>
                        :
                        <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
                            {
                                props.events.map((event, i) => (
                                    <ListItemButton sx={{ bgcolor: props.selectedEvents.includes(event.id) ? 'rgba(0,177,168,0.4)' : 'background.paper' }} onClick={() => {
                                        axiosProtect.get(`/s/sites/get-site-event?eventID=${event.id}`).then(response => {
                                            setEventData(response.data.eventData);
                                            props.setEventInfoModalOpen(true);
                                        }).catch(err => {});
                                    }}>
                                        <ListItemAvatar>
                                        <Avatar>
                                            {
                                                event.category === 'leak' ? <InvertColorsIcon/> : 
                                                event.category === 'production' ? <WarehouseIcon/> : 
                                                event.category === 'malfunction' ? <ConstructionIcon/> : 
                                                event.category === 'alert' ? <PriorityHighIcon/> :
                                                <MiscellaneousServicesIcon/>
                                            }
                                        </Avatar>
                                        </ListItemAvatar>
                                        <ListItemText primary={event.title} secondary={formatEventTime(event.event_time_utc)} />
                                    </ListItemButton>
                                ))
                            }
                        </List>
                    }
                </Accordion.Body>
            </Accordion.Item>

            <Accordion.Item eventKey="2">
                <Accordion.Header onClick={() => toggleAccordionKey('2')}>{`Insights`}</Accordion.Header>
                <Accordion.Body style={{height:`${props.isClient ? '32vh' : '39vh'}`, overflowY:'scroll', scrollbarWidth: 'none', msOverflowStyle: 'none'}}>
                    {
                        (props.data == null ? true : props.data.length === 0) ? 
                        <Box sx={{ display: 'flex' }}>
                            <CircularProgress style={{margin:'auto'}}/>
                        </Box>
                        :
                        <>
                        {
                            props.insights == null ? (
                                <p>Highlight a range of the graph to see a summary of the timeseries data.</p>
                            ) : props.insights === 'error' ? (
                                <p>Error processing insights.</p>
                            ) : typeof props.insights === 'object' ? (
                                Object.keys(props.insights)
                                .sort((a, b) => a.localeCompare(b))
                                .map((comp) => (
                                    <div key={comp}>
                                        <strong>{getInsightName(comp)}</strong>
                                        <p className="m-0">
                                            Min: {Number(Math.round((props.insights[comp].min + Number.EPSILON) * 100) / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")} {determineUnits(false)}
                                        </p>
                                        <p className="m-0">
                                            Max: {Number(Math.round((props.insights[comp].max + Number.EPSILON) * 100) / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")} {determineUnits(false)}
                                        </p>
                                        {/* <p className="m-0"> */}
                                        <p>
                                            Average: {Number(Math.round((props.insights[comp].avg + Number.EPSILON) * 100) / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")} {determineUnits(false)}
                                        </p>
                                        {/* Leave for now, will re-implement once math is discussed */}
                                        {
                                            !['temperature', 'steam'].includes(props.plotSpecs.activeUtility) &&
                                            <p>
                                                Total: {Number(Math.round((props.insights[comp].total + Number.EPSILON) * 100) / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")} {determineUnits(true)}
                                            </p>
                                        }
                                    </div>
                                ))
                            ) : null
                        }
                        </>
                    }
                </Accordion.Body>
            </Accordion.Item>
        </Accordion>
        </>
    );
}

export default DataPanel;